import { Component, OnInit, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first } from 'rxjs/operators';
import { routerTransition } from '../../router.animations';
import { FadeInOutAnimation } from './login.animations';
import { AlertService } from '@/modules/alert';
import { AuthenticationService } from '@/auth';
import { AppConfig } from '@/app.config';
import { NGXLogger } from 'ngx-logger';

// Check https://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial#alert-service-ts

// TODO: Fade-in could be better.
// TODO: Error message for email, password cause the separator line to increase in height

@Component({
    selector: 'app-login',
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.scss'],
    animations: [routerTransition(), FadeInOutAnimation]
})
export class LoginComponent implements OnInit, OnDestroy {
    loginForm: FormGroup;
    loading = false;
    submitted = false;

    pwdChangeForm: FormGroup;
    pwdLoading = false;
    pwdSubmitted = false;

    animationState = 'out';
    warning = '';

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private logger: NGXLogger,
        private authenticationService: AuthenticationService,
        private alertService: AlertService,
        private titleService: Title,
        private appConfig: AppConfig
    ) {
        // redirect to home if already logged in
        if (this.authenticationService.currentUserValue) {
            this.router.navigate(['/']);
        } else {
            this.titleService.setTitle(this.appConfig.loginTitle);
        }
    }

    // convenience getters for easy access to form fields
    get f() {
        return this.loginForm.controls;
    }
    get pf() {
        return this.pwdChangeForm.controls;
    }

    ngOnInit() {
        this.loginForm = this.formBuilder.group({
            email: ['', Validators.required],
            password: ['', Validators.required]
        });
        this.pwdChangeForm = this.formBuilder.group({
            emailid: ['', [Validators.required, Validators.email]]
        });

        const body = document.getElementsByTagName('body')[0];
        body.classList.add('page-signin');
    }

    ngOnDestroy() {
        const body = document.getElementsByTagName('body')[0];
        body.classList.remove('page-signin');
    }

    flipForm() {
        this.animationState = this.animationState === 'out' ? 'in' : 'out';
        this.logger.trace('this.animationState changed to ' + this.animationState);
    }

    onLoginSubmit() {
        this.submitted = true;
        this.warning = '';
        if (this.loginForm.invalid) {
            const f = this.f;
            if (f.email.errors && f.email.errors.required) {
                this.warning = 'Email is required';
            } else if (f.password.errors && f.password.errors.required) {
                this.warning = 'Password is required';
            }
            return;
        }

        this.loading = true;
        this.authenticationService
            .login(this.f.email.value, this.f.password.value)
            .pipe(first())
            .subscribe({
                next: _response => {
                    // get return url from route parameters or default to '/'
                    const returnUrl = this.route.snapshot.queryParams.returnUrl || '/';
                    this.logger.trace('Login: Navigate to ' + returnUrl);
                    this.titleService.setTitle(this.appConfig.title);
                    localStorage.setItem('isLoggedin', 'true');
                    this.router.navigate([returnUrl]);
                },
                error: error => {
                    this.logger.debug('Login Error Encountered: ', error);
                    this.alertService.loginError('Login Error: ' + error.message, {
                        autoClose: true
                    });
                    this.loading = false;
                }
            });
    }

    onPwdChangeSubmit() {
        this.pwdSubmitted = true;
        this.warning = '';

        // stop here if form is invalid
        if (this.pwdChangeForm.invalid) {
            const pf = this.pf;
            if (this.pwdSubmitted && pf.emailid.errors && pf.emailid.errors.required) {
                this.warning = 'Email ID is required';
            }
            return;
        }

        this.pwdLoading = true;
    }
    forgotPwd() {
        this.pwdLoading = true;
        // eslint-disable-next-line no-useless-escape
        const reg = /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/;
        const mail = this.pf.emailid.value;
        if (mail.length === 0) {
            this.pwdLoading = false;
            this.alertService.warn('Please provide a valid email id.', { autoClose: true });
            return false;
        }
        if (mail.length > 0) {
            if (reg.test(mail) === false) {
                this.pwdLoading = false;
                this.alertService.warn('Please provide a valid email id.', { autoClose: true });
                return false;
            } else {
                this.authenticationService
                    .forgotPwd(this.pf.emailid.value)
                    .pipe(first())
                    .subscribe({
                        next: data => {
                            this.pwdLoading = false;
                            this.alertService.success(data.message, { autoClose: true });
                            this.router.navigate(['/login']);
                        },
                        error: error => {
                            this.pwdLoading = false;
                            this.alertService.error(error.message, { autoClose: true });
                            this.loading = false;
                        }
                    });
            }
        }
    }
}
